import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useEffect } from 'react'
import { useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import { IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { Dropdown } from 'core-components/library/dropdown'
import { Input, NumberInput } from 'core-components/library/inputs'
import { LoaderOverlay } from 'core-components/library/loader'
import {
  EWidthTypes,
  TImageSettings,
} from 'core-components/library/richTextEditor/interface/TImage'
import { Controller, ValidationStatus } from 'core-components/library/validation'
import { BaseCrud, Static } from 'core-components/types'

import { EMediaType } from '../common/mediaDragNDrop/interfaces'
import { MediaDragNDrop } from '../common/mediaDragNDrop/MediaDragNDrop'
import { IFormatCrudItem } from '../dropDown/BaseDropDown'

import styles from './ImageSettingsDialog.module.scss'

interface IProps {
  imageSetting: TImageSettings
  onCancel: () => void
  onSave: (imageSettings: TImageSettings) => void
}

export const ImageSettingsDialog = ({ imageSetting, onSave, onCancel }: IProps) => {
  const [widthSizeType, setWidthSizeType] = useState<string>(EWidthTypes.percent)
  const [widthSize, setWidthSize] = useState<number>(100)
  const [editedSettings, setEditedSetting] = useState<TImageSettings>({ ...imageSetting })
  const [wasChanged, setWasChanged] = useState(false)
  const [canSaveImage, setCanSaveImage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState<IFileSettings | null | undefined>(undefined)
  const { fileUploadAdapter } = useSlate()

  const widthTypesSource = useMemo(() => {
    const WidthTypesItems: IFormatCrudItem<string>[] = Object.keys(EWidthTypes).map((key) => ({
      key: key,
      value: EWidthTypes[key],
    }))
    return new BaseCrud({
      fetcher: new Static(WidthTypesItems),
      defaultPaging: {
        page: 0,
        pageSize: WidthTypesItems.length,
      },
      keyField: 'value',
    })
  }, [])

  const onFileLoad = useCallback(
    async (file: IFileSettings) => {
      if (fileUploadAdapter) {
        return fileUploadAdapter(file)
      }
      return ''
    },
    [fileUploadAdapter]
  )

  const updateSettings = useCallback((field: keyof TImageSettings, value: string) => {
    setWasChanged(true)
    setEditedSetting((prevState) => ({ ...prevState, [field]: value }))
  }, [])

  const onFileChange = useCallback((newFile: IFileSettings) => {
    setFile(newFile)
    setWasChanged(true)
  }, [])

  const onSaveClick = useCallback(async () => {
    setIsLoading(true)
    if (file) {
      const url = await onFileLoad(file)
      if (url) {
        updateSettings('url', url)
        onSave({ ...editedSettings, url: url })
      }
    } else {
      onSave(editedSettings)
    }
    setIsLoading(false)
  }, [file, onFileLoad, updateSettings, onSave, editedSettings])

  useEffect(() => {
    if (file !== undefined) {
      updateSettings('url', '')
    }
  }, [file, updateSettings])

  useEffect(() => {
    updateSettings('width', widthSize + widthSizeType)
  }, [widthSizeType, widthSize, updateSettings])

  useEffect(() => {
    if (imageSetting.width) {
      const splitedWidthSize = imageSetting.width.match(/[a-zA-Z]+|[0-9]+/g)
      if (splitedWidthSize) {
        const [size, type] = splitedWidthSize
        setWidthSize(parseFloat(size))
        if (EWidthTypes[type]) {
          setWidthSizeType(type as EWidthTypes)
        }
      }
    }
  }, [imageSetting.width])

  return (
    <div>
      <LoaderOverlay show={isLoading} />
      <Controller
        onValidationStatusChange={(result) => {
          setCanSaveImage(result.status === ValidationStatus.Valid)
        }}
      >
        <div className={styles.root}>
          <div className={classNames(styles.body, styles.settings)}>
            <div className={styles.settings__line}>
              <div className={styles.settingsLine__label}>Width:</div>
              <div className={styles.settingsLine__combineValue}>
                <NumberInput
                  value={widthSize}
                  placeholder={'Add width'}
                  onChange={(value) => {
                    setWidthSize(value ? parseFloat(value) : 0)
                  }}
                  min={0}
                />
                <Dropdown
                  source={widthTypesSource}
                  caption={'value'}
                  label={'Width type'}
                  onChange={(value) => setWidthSizeType(value as EWidthTypes)}
                  value={widthSizeType}
                />
              </div>
            </div>
            <div className={styles.settings__line}>
              <div className={styles.settingsLine__label}>Alternative text:</div>
              <Input
                value={editedSettings.alt}
                placeholder={'Add alternative text'}
                onChange={(value) => updateSettings('alt', value)}
                classes={{ root: styles.settingsLine__value }}
              />
            </div>
            <div className={styles.settings__line}>
              <div className={styles.settingsLine__label}>Caption:</div>
              <Input
                value={editedSettings.caption}
                placeholder={'Add caption'}
                onChange={(value) => updateSettings('caption', value)}
                classes={{ root: styles.settingsLine__value }}
              />
            </div>
            <MediaDragNDrop
              onUrlChange={(value) => updateSettings('url', value)}
              fileUrl={editedSettings.url}
              type={EMediaType.Image}
              onFileChange={onFileChange}
            />
          </div>
          <div className={styles.footer}>
            <Button
              onClick={onSaveClick}
              disabled={!wasChanged || !canSaveImage}
              className={styles.footer__button}
            >
              Save
            </Button>
            <Button className={styles.footer__button} onClick={onCancel} color={'danger'}>
              Close
            </Button>
          </div>
        </div>
      </Controller>
    </div>
  )
}
